import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { FormSpy } from 'react-final-form';
import { makeStyles, withStyles } from 'tss-react/mui';
import { Modal } from '@jucy-ui/components/Modal';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import { MenuProps } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MuiLink from '@mui/material/Link';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { runInAction } from 'mobx';
import { useAuthenticatedAppApi } from '../../hooks/useAuthenticatedAppApi';
import { useRootStore } from '../../hooks/useRootStore';
import { exceptionToFormError } from '../../lib/Errors';
import { clearSelfServiceSession } from '../../lib/selfServiceSession';
import UserProfile from '../../stores/models/UserProfile';
import ButtonCircularProgress from '../ButtonCircularProgress';
import UserAvatar from '../UserAvatar';
import UserDetailsFrom from '../UserMenu/UserDetailsForm';
import UserDetailsFromFields from '../UserMenu/UserDetailsFormFields';

const useStyles = makeStyles()((theme) => ({
    styledMenuItem: {
        '&:active': {
            backgroundColor: theme.palette.primary.main,
            '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                color: theme.palette.common.white,
            },
        },
    },
}));

const StyledMenu = withStyles(
    (props: MenuProps) => (
        <Menu
            elevation={0}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            {...props}
        />
    ),
    {
        paper: {
            border: '1px solid #d3d4d5',
        },
    }
);

const UserMenu: React.FC<React.PropsWithChildren<unknown>> = () => {
    const appApiClient = useAuthenticatedAppApi();
    const rootStore = useRootStore();
    const { classes } = useStyles();
    const [successMessage, setSuccessMessage] = useState(false);
    const handleClose = () => {
        runInAction(() => {
            rootStore.appStateStore.profileModalOpen = false;
            setSuccessMessage(false);
        });
    };
    const isSocialLogin = !rootStore?.authStore?.profile?.userId?.startsWith('auth0');

    return (
        <>
            <Modal
                open={rootStore.appStateStore.profileModalOpen}
                onClose={handleClose}
                aria-labelledby="form-dialog-title"
                title="Your account"
                titleIcon={<PermIdentityIcon />}
            >
                {rootStore.authStore.profile && appApiClient ? (
                    <UserDetailsFrom
                        user={rootStore.authStore.profile}
                        onSubmit={(updatedProfile) =>
                            appApiClient
                                .updateProfile(updatedProfile)
                                .then((p) => {
                                    rootStore.authStore.profile = new UserProfile(p);
                                    setSuccessMessage(true);
                                })
                                .catch(exceptionToFormError)
                        }
                    >
                        <DialogContent>
                            <UserDetailsFromFields user={rootStore.authStore.profile} />
                        </DialogContent>
                        {successMessage && <Alert severity="success">Profile updated successfully</Alert>}
                        <DialogActions>
                            <FormSpy subscription={{ submitting: true, submitError: true, hasSubmitErrors: true }}>
                                {({ submitting, submitError, hasSubmitErrors }) => (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                        }}
                                    >
                                        {hasSubmitErrors && submitError ? <Alert severity="error">{submitError}</Alert> : null}
                                        &nbsp;
                                        {isSocialLogin ? null : (
                                            <Button variant="contained" color="primary" type="submit">
                                                Update
                                                <ButtonCircularProgress isSubmitting={submitting} color={'secondary'} />
                                            </Button>
                                        )}
                                        &nbsp;
                                        <Button disabled={submitting} onClick={handleClose}>
                                            Close
                                        </Button>
                                    </Box>
                                )}
                            </FormSpy>
                        </DialogActions>
                    </UserDetailsFrom>
                ) : null}
            </Modal>
            <PopupState variant="popover" popupId="user-menu">
                {(popupState) => (
                    <>
                        <IconButton color="inherit" {...bindTrigger(popupState)} size="large">
                            <UserAvatar profile={rootStore.authStore.profile as UserProfile} />
                        </IconButton>
                        <StyledMenu {...bindMenu(popupState)}>
                            <MenuItem
                                onClick={() => {
                                    popupState.close();
                                    runInAction(() => (rootStore.appStateStore.profileModalOpen = true));
                                }}
                                className={classes.styledMenuItem}
                            >
                                <ListItemIcon>
                                    <AccountCircleIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary="Account" />
                            </MenuItem>
                            <Divider />
                            <MenuItem href="/logout" component={MuiLink} onClick={clearSelfServiceSession} className={classes.styledMenuItem}>
                                <ListItemIcon>
                                    <ExitToAppIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary="Sign out" />
                            </MenuItem>
                        </StyledMenu>
                    </>
                )}
            </PopupState>
        </>
    );
};

export default observer(UserMenu);
